import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ORG_BILLING_ENABLED } from "@/lib/featureFlags";
import { cn } from "@/lib/utils";
import { useOrgStore } from "@/store/orgStore";
import { Building2, Check, ChevronDown, Plus, Settings } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";

/**
 * OrgSwitcher — compact dropdown in the sidebar header.
 *
 * Shows "Personal" when no org is selected. When the user belongs to orgs,
 * a chevron appears and the dropdown lists all orgs + "Personal only".
 *
 * Per plan: personal workspace is always visible and pinned at the top
 * of the sidebar. The org switcher only changes which **team** workspaces
 * appear below the personal section.
 */
export const OrgSwitcher = React.memo(function OrgSwitcher() {
  const navigate = useNavigate();
  const orgs = useOrgStore((s) => s.orgs);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const setActiveOrgId = useOrgStore((s) => s.setActiveOrgId);

  // Feature-flagged: hide entire component when org billing is disabled
  if (!ORG_BILLING_ENABLED) return null;

  // If user has no orgs, don't render
  if (!orgs || orgs.length === 0) return null;

  const activeOrg = orgs.find((o) => o.org_id === activeOrgId);
  const label = activeOrg ? activeOrg.org_name : "Personal";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex items-center gap-1.5 rounded-md px-2 py-1",
          "text-xs font-medium text-sidebar-foreground/70",
          "hover:bg-sidebar-accent hover:text-sidebar-foreground",
          "transition-colors focus-visible:outline-none",
          "focus-visible:ring-2 focus-visible:ring-purple-400",
          "w-full group-data-[collapsible=icon]:hidden"
        )}
      >
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
        <ChevronDown className="h-3 w-3 ml-auto shrink-0 opacity-50" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="bottom" className="w-56">
        {/* Personal only */}
        <DropdownMenuItem
          onClick={() => setActiveOrgId(null)}
          className="flex items-center gap-2"
        >
          <span className="flex-1 truncate">Personal</span>
          {!activeOrgId && <Check className="h-4 w-4 shrink-0" />}
        </DropdownMenuItem>

        {orgs.length > 0 && <DropdownMenuSeparator />}

        {/* Org list */}
        {orgs.map((org) => (
          <DropdownMenuItem
            key={org.org_id}
            onClick={() => setActiveOrgId(org.org_id)}
            className="flex items-center gap-2"
          >
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 truncate">{org.org_name}</span>
            {activeOrgId === org.org_id && (
              <Check className="h-4 w-4 shrink-0" />
            )}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        {/* Actions */}
        {activeOrgId && (
          <DropdownMenuItem
            onClick={() => navigate(`/org/${activeOrgId}/settings`)}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span>Organization Settings</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onClick={() => navigate("/org/new")}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>Create Organization</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
